import React, { FC, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentEvents,
  ThreadPinnedEvent,
  ThreadUnpinnedEvent,
  ThreadArchivedEvent,
} from '@dreamai/types';
import api from '../../api';
import { usePusher } from '../../contexts/PusherContext';
import { ReactComponent as ShareIcon } from '../../icons/share.svg';
import { ReactComponent as PinIcon } from '../../icons/pin.svg';
import { ReactComponent as UnpinIcon } from '../../icons/unpin.svg';
import { ReactComponent as TrashIcon } from '../../icons/trash-rounded.svg';
import ThreadShareModal from './sidebar/ThreadShareModal';
import ConfirmModal from '../confirm-modals/ConfirmModal';
import './chat-header.scss';

interface ChatHeaderProps {
  threadId: string | null;
}

const ChatHeader: FC<ChatHeaderProps> = ({ threadId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const isNewConversation = searchParams.get('new') === 'true';
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;

  // Thread info state
  const [threadDescription, setThreadDescription] = useState<
    string | undefined
  >();
  const [isMainThread, setIsMainThread] = useState(false);
  const [isInternalMonologue, setIsInternalMonologue] = useState(false);
  const [isViaCompany, setIsViaCompany] = useState(false);
  const [sharePermission, setSharePermission] = useState<string | undefined>();
  const [companyPermission, setCompanyPermission] = useState<
    string | undefined
  >();
  const [hasShares, setHasShares] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isCampaignThread, setIsCampaignThread] = useState(false);
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isArchivedThread, setIsArchivedThread] = useState(false);

  // Fetch thread info
  const fetchThreadInfo = useCallback(async () => {
    if (!threadId) {
      setThreadDescription(undefined);
      setIsMainThread(false);
      setIsInternalMonologue(false);
      setIsViaCompany(false);
      setSharePermission(undefined);
      setCompanyPermission(undefined);
      setHasShares(false);
      setIsPinned(false);
      setIsOwner(false);
      setIsCampaignThread(false);
      setCampaignId(null);
      setIsArchivedThread(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get(`/agents/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.thread) {
        const thread = response.data.thread;
        setThreadDescription(thread.description);
        setIsMainThread(thread.is_main_thread || false);
        setIsInternalMonologue(thread.is_internal_monologue || false);
        setIsViaCompany(response.data.via_company || false);
        setSharePermission(response.data.share_permission);
        setCompanyPermission(response.data.company_permission);
        setHasShares(response.data.has_shares || response.data.via_company);
        setIsOwner(response.data.share_permission === 'OWNER');
        setIsArchivedThread(thread.is_archived_thread || false);
        // Check if this is a campaign thread and get the campaignId
        const campaignDiscussingObject = thread.discussing_objects?.find(
          (obj: { discussing_object: string }) =>
            obj.discussing_object === 'Campaign',
        );
        setIsCampaignThread(!!campaignDiscussingObject);
        setCampaignId(
          campaignDiscussingObject?.discussing_object_match?.campaignId ?? null,
        );
      }
    } catch (error) {
      console.error('Error fetching thread info:', error);
    }
  }, [threadId, getAccessTokenSilently]);

  // Refresh shares status by re-fetching thread info
  const refreshThreadShares = useCallback(() => {
    fetchThreadInfo();
  }, [fetchThreadInfo]);

  // Fetch pinned status
  const fetchPinnedStatus = useCallback(async () => {
    if (!threadId) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get('/agents/threads/pinned', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setIsPinned(response.data.pinnedThreadIds.includes(threadId));
      }
    } catch (error) {
      console.error('Error fetching pinned status:', error);
    }
  }, [threadId, getAccessTokenSilently]);

  // Toggle pin status (optimistic update)
  const handleTogglePin = useCallback(async () => {
    if (!threadId) return;

    const previousState = isPinned;
    // Optimistically update UI immediately
    setIsPinned(!isPinned);

    try {
      const token = await getAccessTokenSilently();
      const endpoint = previousState
        ? '/agents/threads/unpin'
        : '/agents/threads/pin';

      await api.post(
        endpoint,
        { threadId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (error) {
      console.error('Error toggling pin:', error);
      // Revert on error
      setIsPinned(previousState);
    }
  }, [threadId, isPinned, getAccessTokenSilently]);

  useEffect(() => {
    fetchThreadInfo();
    fetchPinnedStatus();
  }, [fetchThreadInfo, fetchPinnedStatus]);

  // Listen for pin/unpin events for real-time sync across devices
  useEffect(() => {
    if (!userChannel || !threadId) return;

    const handleThreadPinned = (data: ThreadPinnedEvent) => {
      if (data.threadId === threadId) {
        setIsPinned(true);
      }
    };

    const handleThreadUnpinned = (data: ThreadUnpinnedEvent) => {
      if (data.threadId === threadId) {
        setIsPinned(false);
      }
    };

    userChannel.bind(AgentEvents.THREAD_PINNED, handleThreadPinned);
    userChannel.bind(AgentEvents.THREAD_UNPINNED, handleThreadUnpinned);

    return () => {
      userChannel.unbind(AgentEvents.THREAD_PINNED, handleThreadPinned);
      userChannel.unbind(AgentEvents.THREAD_UNPINNED, handleThreadUnpinned);
    };
  }, [userChannel, threadId]);

  // Handle archive thread
  const handleArchive = useCallback(async () => {
    if (!threadId) return;

    setIsArchiving(true);
    try {
      const token = await getAccessTokenSilently();

      // Use campaign delete endpoint for campaign threads
      if (isCampaignThread && campaignId) {
        await api.delete(`/campaigns/${campaignId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await api.post(
          '/agents/threads/archive',
          { threadId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      // Reload to show archived state
      window.location.reload();
    } catch (error: any) {
      console.error('Error archiving thread:', error);
      alert(error.response?.data?.error || 'Failed to delete');
    } finally {
      setIsArchiving(false);
      setShowArchiveConfirm(false);
    }
  }, [threadId, isCampaignThread, campaignId, getAccessTokenSilently]);

  // Determine if archive button should be shown
  // Show for owners of non-main, non-internal-monologue, non-archived threads (including campaigns)
  const canArchive =
    isOwner && !isMainThread && !isInternalMonologue && !isArchivedThread;

  // Determine if share button should be disabled
  // Note: isViaCompany is NOT disabled - users should be able to see the company-wide access info
  const shareDisabled =
    isMainThread ||
    isInternalMonologue ||
    isArchivedThread ||
    sharePermission === 'READ' ||
    sharePermission === 'WRITE';

  // Get display title
  const getTitle = () => {
    if (isNewConversation) {
      return 'New Chat';
    }
    if (
      threadDescription &&
      !threadDescription.toLowerCase().startsWith('conversation')
    ) {
      return threadDescription;
    }
    return 'Chat';
  };

  return (
    <div className='chat-header'>
      <h1 className='chat-title'>{getTitle()}</h1>

      {threadId && !isNewConversation && (
        <div className='actions'>
          {/* Archive button - only for owners of non-main, non-campaign threads */}
          {canArchive && (
            <button
              className='archive-btn'
              onClick={() => setShowArchiveConfirm(true)}
              title='Delete thread'
            >
              <TrashIcon />
            </button>
          )}

          {!isArchivedThread && (
            <button
              className={`share-btn ${shareDisabled ? 'disabled' : ''} ${hasShares || isViaCompany ? 'has-shares' : ''}`}
              onClick={() => !shareDisabled && setShowShareModal(true)}
              disabled={shareDisabled}
            >
              <ShareIcon />
            </button>
          )}

          {/* Hide pin button for hotline/main threads. Allow unpin for archived threads that are pinned */}
          {!isMainThread && (!isArchivedThread || isPinned) && (
            <button
              className={`pin-btn ${isPinned ? 'pinned' : ''}`}
              onClick={handleTogglePin}
              title={isPinned ? 'Unpin thread' : 'Pin thread'}
            >
              {isPinned ? <UnpinIcon /> : <PinIcon />}
            </button>
          )}

          {showShareModal && (
            <ThreadShareModal
              threadId={threadId}
              isOwner={sharePermission === 'OWNER'}
              isViaCompany={isViaCompany}
              sharePermission={sharePermission}
              companyPermission={companyPermission}
              isMainThread={isMainThread}
              isInternalMonologue={isInternalMonologue}
              onClose={() => {
                setShowShareModal(false);
                refreshThreadShares();
              }}
            />
          )}

          {/* Archive confirmation modal */}
          <ConfirmModal
            show={showArchiveConfirm}
            handleClose={() => setShowArchiveConfirm(false)}
            handleConfirm={handleArchive}
            title={isCampaignThread ? 'Delete Campaign' : 'Delete Thread'}
            bodyText={
              isCampaignThread
                ? "You're about to delete this campaign and its thread. This action cannot be undone."
                : "You're about to delete this thread. This action cannot be undone."
            }
            confirmLabel={isArchiving ? 'Deleting...' : 'Delete'}
            cancelLabel='Cancel'
            confirmDisabled={isArchiving}
            singleButton={false}
            verticalButtons={false}
            littleSweetieVersion={false}
            confirmFirst={false}
          />
        </div>
      )}
    </div>
  );
};

export default ChatHeader;
